import { v4 as uuidv4, validate as uuidValidate } from 'uuid';
import { BaseRestRepository } from './baseRestRepository';
import type { AIChatKey } from '~/components/Onboarding/V2/config';

export type ChatHistoryMessage = {
  chatId: string;
  created: string;
  id: string;
  message: string;
  sender: 'Ai' | 'User';
  updated: string;
  injectionKey?: string;
};

export type ChatHistoryResponse = ChatHistoryMessage[];

interface SendMessageRequest {
  message: string;
  formKey?: AIChatKey;
}

interface InjectMessageRequest {
  key: string;
}

const ONBOARDING_AI_URL = '/onboardingai/api';

export class OnboardingAiRepository extends BaseRestRepository {
  generateChatId(): string {
    return uuidv4();
  }

  validateChatId(chatId: string): boolean {
    return uuidValidate(chatId);
  }

  async getChatHistory(chatId: string): Promise<ChatHistoryResponse> {
    if (!this.validateChatId(chatId)) {
      throw new Error('Invalid chatId format. Expected UUID.');
    }
    const response = await this.fetchWithAuth(`${ONBOARDING_AI_URL}/chats/${chatId}`);
    return this.getJsonResponse(response);
  }

  async sendMessage(chatId: string, request: SendMessageRequest): Promise<void> {
    if (!this.validateChatId(chatId)) {
      throw new Error('Invalid chatId format. Expected UUID.');
    }
    await this.fetchWithAuth(`${ONBOARDING_AI_URL}/chats/${chatId}`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async injectMessage(chatId: string, request: InjectMessageRequest): Promise<void> {
    if (!this.validateChatId(chatId)) {
      throw new Error('Invalid chatId format. Expected UUID.');
    }
    await this.fetchWithAuth(`${ONBOARDING_AI_URL}/chats/${chatId}/injections`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getMessageVoice(chatId: string, messageId: string): Promise<Blob> {
    if (!this.validateChatId(chatId)) {
      throw new Error('Invalid chatId format. Expected UUID.');
    }
    const response = await this.fetchWithAuth(`${ONBOARDING_AI_URL}/chats/${chatId}/messages/${messageId}/voice`);
    return response.blob();
  }

  async getPreRecordedVoice(key: string): Promise<Blob> {
    const response = await this.fetchWithAuth(`${ONBOARDING_AI_URL}/voices/injections/${key}`);
    return response.blob();
  }
}
